/**
 * GodMode — runtime-tunable master parameters for game balancing.
 * All values default to 1.0 (neutral multiplier) or their game defaults.
 * The GodMode panel in UIScene exposes sliders to tweak these live.
 *
 * Usage: import { GOD } from '@/config/GodMode';
 *        const finalDamage = baseDamage * GOD.robotDamageMult;
 */

import { getOverridesSnapshot, importOverridesSnapshot, saveComponentOverrides } from '@/config/ComponentOverrides';
import { registerProfileSyncCallback } from '@/config/ProfileSync';
import { saveCurve } from '@/systems/WaveComposer';

export const GOD = {
  // ── Robot multipliers ──
  robotHpMult: 1.0,           // multiplier on all robot HP
  robotEnergyMult: 1.0,       // multiplier on starting energy capacity
  robotEnergyDrainMult: 1.0,  // multiplier on energy consumption rate
  robotSpeedMult: 1.0,        // multiplier on robot movement speed
  robotDamageMult: 1.0,       // multiplier on robot weapon damage
  robotPassiveRechargeRate: 0.01, // base passive energy regen (% of max per second, no recharge module)
  kamikazeDetonationFactor: 0.35, // detonation distance = explosionRadius × this factor (capped at 24px)
  kamikazeAoeRadiusMult: 2.0,     // multiplier on explosion AoE radius
  kamikazeFalloffPower: 1.0,      // falloff exponent: 1=linear, 2=quadratic (sharper drop), 0.5=sqrt (gentle)

  // ── Enemy multipliers ──
  enemyHpMult: 1.0,           // multiplier on all enemy HP
  enemySpeedMult: 1.0,        // multiplier on enemy movement speed
  enemyDamageMult: 1.0,       // multiplier on enemy damage to base & robots
  enemyWeaponRangeMult: 1.0,  // multiplier on enemy weapon firing range
  // ── Close-in heuristic (break orbit deadlock) ──
  closeInDelay: 1.5,           // seconds without firing before close-in kicks in
  closeInMaxShrink: 0.4,       // max orbit shrink factor (0.4 = orbit goes to 40% of normal)
  closeInRampTime: 2.0,        // seconds to ramp from 0% to max shrink after delay
  enemySteerSmoothing: 0.15,   // exponential smoothing for ranged steering (0=frozen, 1=instant, 0.15=smooth)
  wallDisengageTime: 3.0,      // seconds without firing before enemy drops aggro (wall stuck disengage)

  // ── Base turret ──
  baseTurretDamageMult: 1.0,    // multiplier on base turret projectile damage
  baseTurretRangeMult: 1.0,     // multiplier on base turret detection range

  // ── Base upgrades ──
  baseUpgradeCostMult: 1.0,     // multiplier on all base upgrade resource costs

  // ── Armor ──
  armorShieldMult: 1.0,       // multiplier on armor shield HP
  armorRicochetMult: 1.0,     // multiplier on ricochet chance (capped at 1.0)

  // ── Evolution tier tradeoffs (Pattern B) ──
  tierMassMult: 0.3,          // mass increase per evolution tier (+30%/tier)
  tierEnergyDrainMult: 0.2,   // energy drain increase per evolution tier (+20%/tier)

  // ── Economy ──
  startingMoney: 70,          // starting currency for new campaign
  challengeStartingMoney: 70, // starting currency for challenge mode
  challengeRewardBase: 30,    // base $ reward for challenge victory
  challengeRewardPerWave: 8,  // extra $ per wave completed in challenge
  challengeResourceMult: 0.5, // fraction of Co/Electro earned in challenge that persists
  resourcesMult: 1.0,         // multiplier on all resource gains (kills, wave bonus)
  excavatorDpsMult: 1.0,      // multiplier on excavator mining DPS (base 8 DPS)
  resourceWeaponResist: 0.95, // weapon/explosion damage reduction on resources (0.95 = 95%)
  componentCostMult: 1.0,     // multiplier on component placement cost
  modificationFeePercent: 0.20, // fee charged when modifying a placed robot (% of original cost)
  modificationFeeMin: 5,        // minimum modification fee ($)

  // ── Terrain ──
  tileRepairCostMult: 1.0,    // multiplier on cracked tile repair cost

  // ── Workshop ──
  workshopPriceMult: 1.0,     // multiplier on all workshop prices (assembly, unlock, upgrade)

  // ── Wave ──
  spawnDelayMult: 1.0,        // multiplier on spawn delay (higher = slower spawns)
  waveCountMult: 1.0,         // multiplier on enemy count per wave

  // ── Display: Tiles ──
  tileSaturation: 1.0,        // tile saturation (1 = full color, 0 = grayscale)
  tileBrightness: 0.0,        // tile brightness offset (-1 dark .. +1 bright)
  tileContrast: 1.0,          // tile contrast (0 = grey, 1 = normal, >1 = high)
  tileHue: 0.0,               // tile hue rotation in degrees (-180 .. 180)
  tileDarken: 0.3,            // tile darken overlay opacity (0 = bright, 1 = black)
  // ── Display: Decorations ──
  decorSaturation: 1.0,
  decorBrightness: 0.0,
  decorContrast: 1.0,
  decorHue: 0.0,
  decorDarken: 0.0,
  // ── Display: Resources (Connardium, Garbage, Barrel, Caisse) ──
  resSaturation: 1.0,
  resBrightness: 0.0,
  resContrast: 1.0,
  resHue: 0.0,
  resDarken: 0.0,

  // ── Visual ──
  enemyAnimations: true,      // enable spritesheet walk/attack/death animations on enemies
  enemyScaleMult: 1.0,        // multiplier on enemy sprite display size
  robotScaleMult: 1.0,        // multiplier on robot tile scale (ROBOT_TILE_SCALE)
  componentSpacingMult: 1.0,  // multiplier on COMPONENT_SPACING between parts
  robotBarOffsetMult: 1.0,    // multiplier on robot HP/energy bar distance
  robotBarSizeMult: 1.0,      // multiplier on robot HP/energy bar width & height
  enemyBarOffsetMult: 1.0,    // multiplier on enemy HP bar distance
  enemyBarSizeMult: 1.0,      // multiplier on enemy HP bar width & height
  flipCooldownMs: 150,        // min ms between direction flips (anti-flicker)
  flipDeadzone: 6,            // min px delta-X to trigger a direction flip
  popupStartOffsetMult: 1.0,  // multiplier on popup text spawn distance from unit
  popupSizeMult: 1.0,         // multiplier on popup text font size
  popupDistMult: 1.0,         // multiplier on floating text travel distance (damage, $, combo)
  popupDurationMult: 1.0,     // multiplier on floating text animation duration
  coinFlyPopHeight: 40,       // initial pop height from enemy (px)
  coinFlyBounceCount: 3,      // number of ground bounces (randomized ±1)
  coinFlyBounceDuration: 180, // duration per bounce (ms, decreases each bounce)
  coinFlyBounceDamping: 0.55, // height reduction per bounce (0-1)
  coinFlySpinSpeed: 3,        // 3D spin cycles during bounce phase
  coinFlyDuration: 500,       // coin flight to HUD duration (ms)
  coinFlyArcHeight: 80,       // arc peak offset during flight (px)
  coinFlyIconSize: 40,        // flying coin icon size (px)
  coinFlyHudPulseScale: 1.3,  // HUD coin icon pulse scale on arrival
  coinFlyStaggerDelay: 50,    // stagger between simultaneous coins (ms)
  repairPanTime: 300,         // camera travel time during repair (ms)
  repairShowTime: 500,        // repair text display time (ms)
  repairZoom: 2.5,            // camera zoom during repair sequence
  waveEnergyRecharge: 0.50,   // base energy recovery (% of max) at end of wave — independent of streak
  waveHpRecharge: 0.15,       // base HP recovery (% of max) at end of wave — independent of streak
  cameraZoomMult: 1.0,        // multiplier on dynamic camera zoom (higher = closer)
  cameraContestedRatio: 0.6,  // if 2nd cluster weight >= heaviest × this, camera zooms out to show both
  cameraFollowZoom: 2.0,      // zoom level when camera follows a selected robot
  cameraFollowLerp: 3.0,      // lerp speed for camera follow (higher = snappier tracking)
  impactSlowmo: true,         // enable slow-motion on high-impact events (enemy kill, robot death, etc.)
  useSpriteUI: false,         // use PixelLab sprite UI (NineSlice) instead of procedural TechUI

  // ── HUD top bar positions ──
  hudEnemiesX: 120,           // X position of "Enemies:" label
  hudBaseX: 250,              // X position of "Base:" label
  hudBarX: 310,               // X position of base HP bar
  hudBarW: 90,                // width of base HP segmented bar
  hudBarH: 14,                // height of base HP segmented bar

  // ── HUD Base HP bar ──
  baseHpSegmentPct: 10,       // segment size in % of max HP (5, 10, 20…)
  hpBarFrameW: 240,           // HP bar frame display width
  hpBarFrameH: 44,            // HP bar frame display height
  hpBarFrameOffX: 0,          // HP bar frame X offset from bar center
  hpBarFrameOffY: 0,          // HP bar frame Y offset from bar center

  // ── Projectiles ──
  projectileScaleMult: 1.0,   // multiplier on projectile sprite size
  trailLengthMult: 1.0,       // multiplier on projectile trail length (stored positions)

  // ── Debug / Overlay ──
  showHeatmap: false,         // display action density heatmap overlay
  showEdgeZones: false,       // display mouse edge-scroll zones overlay
  showMissileArc: false,      // display predicted missile arc trajectory before launch

  // ── Deployment ──
  baseDeployRange: 3,         // base deployment anchor range in tiles (mobile/flying robots can deploy within this range of a base)

  // ── Vision ──
  eyesightRange: 57,          // close-range eyesight radius (px). Default = TILE_SIZE × √2 ≈ 57

  // ── Low Energy ──
  lowEnergyThreshold: 0.20,   // energy ratio below which mobile/flying robots slow down (0..1)
  lowEnergySpeedMult: 0.10,   // speed multiplier when energy is below threshold (0.10 = 10%)

  // ── Flying AI ──
  flyerAudacity: 2.0,         // patrol distance falloff exponent (higher = stays closer to base)
  flyerEdgeRepulsion: 200,    // max edge repulsion acceleration (px/s²)
  flyerEdgeMargin: 3,         // edge repulsion zone width (tiles)
  flyAltitudeSpeed: 1.2,      // altitude bobbing speed (rad/s)
  flyAltitudeScaleAmp: 0.06,  // altitude scale amplitude (±%)
  flyAltitudeShadowAmp: 6,    // altitude shadow offset amplitude (px)

  // ── Cheats ──
  infiniteEnergy: false,      // robots never run out of energy
  infiniteHp: false,          // robots cannot be destroyed
  oneHitKill: false,          // enemies die in one hit
  unlockAll: false,           // all components available
  freeBuilding: false,        // components cost 0 to place
  cpuBot: false,              // AI plays the game automatically
  forceTutorial: false,       // force tutorial to show on next GameScene load

  // ── Cinematic tutorial ──
  cinematicPanTime: 800,           // camera pan duration (ms)
  cinematicBubbleDuration: 3000,   // auto-dismiss bubble time after typewriter (ms)
  cinematicCursorSpeed: 400,       // fake cursor move duration (ms)
  cinematicSpeedMult: 1.0,         // global speed multiplier for all cinematic timings
  forceCinematic: false,           // force cinematic on next load

  // ── Billiard Knockback ──
  billiardEnabled: true,          // master toggle (false = old instant knockback)
  billiardFriction: 3.0,          // multiplicative velocity decay per second
  billiardWallDamping: 0.7,       // velocity retention on wall bounce (0-1)
  billiardEnemyDamping: 0.85,     // velocity retention on enemy-enemy collision (0-1)
  billiardCollisionDamage: 0.1,   // damage = relativeSpeed × this × targetMass / 50
  billiardMaxBounces: 5,          // max wall bounces per enemy per knockback event
  billiardMaxChainDepth: 3,       // max chain propagation depth
  billiardMaxSimTime: 2.0,        // hard cutoff in seconds
  billiardHeavyResistMult: 0.3,   // velocity reduction for mass >= KNOCKBACK_IMMUNE_MASS
  billiardMinSpeed: 5,            // speed below which knockback ends (px/s)
  billiardSpeedMult: 2.5,         // velocity = distance × this (lower = slower pushback)
  billiardSpreadMinDeg: 5,        // min angular spread on knockback direction (degrees)
  billiardSpreadMaxDeg: 20,       // max angular spread on knockback direction (degrees)

  // ── Mud Launcher ──
  mudSplashRadius: 30,            // mud splash radius on impact (px) — tiles within this get converted
  mudTileDuration: 0,             // seconds before mud reverts to original tile (0 = permanent)
  mudDamageMult: 1.0,             // multiplier on mud launcher damage

  // ── Hive enemy ──
  hiveWaspMinCount: 2,            // min Wasps spawned on Hive death
  hiveWaspMaxCount: 4,            // max Wasps spawned on Hive death
  hivePoisonDuration: 5,          // acid poison DoT duration (seconds)
  hivePoisonDps: 4,               // acid poison damage per second per stack
  hivePoisonMaxStacks: 4,         // max concurrent poison stacks on a robot
  hiveAcidSplashRadius: 45,       // acid catapult AoE splash radius (px)

  // ── Healer enemy ──
  healerRange: 120,               // healing aura radius (px)
  healerCooldown: 1.5,            // seconds between heal pulses
  healerAmount: 12,               // HP restored per pulse per ally
  healerMaxTargets: 4,            // max allies healed per pulse
  healerSelfHeal: false,          // whether healer heals itself

  // ── Jumper tuning ──
  jumperDetectRange: 1.0,         // detection range multiplier (× TILE_SIZE from jumper world pos)
  jumperPushTilesBase: 2,         // base push distance in tiles (level 0)
  jumperPushTilesPerLevel: 1,     // extra tiles per evolution level
  jumperMaxKnockbackTiles: 5,     // hard cap on effective push tiles

  // ── Combat tuning ──
  comboWindow: 2.0,                // seconds to accumulate weapon combo hits
  comboMult2: 1.4,                 // combo damage multiplier for 2 weapon types
  comboMult3: 1.8,                 // combo damage multiplier for 3 weapon types
  comboMult4: 2.5,                 // combo damage multiplier for 4+ weapon types
  pathRecalcInterval: 0.5,         // minimum seconds between enemy path recalculations
  terrainDeathWater: 2.5,          // seconds for enemy to die in water
  terrainDeathMagma: 3.0,          // seconds for enemy to die in magma
  terrainDeathChasm: 1.0,          // seconds for enemy to die in chasm
  meleeRangeMult: 1.5,             // multiplier on melee range for point-blank check
  enemyRobotGap: 8,               // minimum pixel gap between enemy and robot bodies (physical push)
  rangedKeepDistPx: 40,           // minimum px ranged enemies maintain from robot edge (hard repulsion)
  weaponCooldownStagger: 0.5,      // multiplier on weapon cooldown for initial stagger & lock

  // ── Evolution scaling ──
  evoFireRateScale: 0.5,           // fire rate scales at this fraction of stat multiplier
  evoSupportRangeBase: 1.5,        // support module range multiplier base per level
  evoSupportDrainBase: 0.85,       // support module drain reduction base per level

  // ── Sensor / Direction component ──
  sensorRangeMult0: 1.5,           // direction component range multiplier (level 0)
  sensorRangeMult1: 1.6,           // direction component range multiplier (level 1)
  sensorRangeMult2: 1.8,           // direction component range multiplier (level 2)
  sensorAccBonus0: 0.2,            // direction component accuracy bonus (level 0)
  sensorAccBonus1: 0.3,            // direction component accuracy bonus (level 1)
  sensorAccBonus2: 0.4,            // direction component accuracy bonus (level 2)

  // ── Detection ──
  detectionRangeMult: 1.5,         // blocker detection range multiplier (× TILE_SIZE)

  // ── Stability: Foundation weights ──
  stabFoundationEnabled: true,            // master toggle for base component type penalty
  stabFoundationMin: 0.2,                 // minimum foundation factor (floor)
  stabFoundationMax: 1.15,                // maximum foundation factor (cap)
  stabFoundationMobileInfluence: 0.3,     // how much foundation affects wheeled robots (0=none, 1=full)
  stabFoundationWeightStructure: 1.2,     // STRUCTURE, ARMOR
  stabFoundationWeightBracket: 1.1,       // STRUCTURE_TRIANGLE
  stabFoundationWeightBattery: 1.1,       // BATTERY
  stabFoundationWeightNeutral: 1.0,       // DIRECTION, RECHARGE_MODULE
  stabFoundationWeightSensor: 0.4,        // SENSOR_SHORT, SENSOR_MEDIUM
  stabFoundationWeightWeapon: 0.3,        // all weapon category types
  stabFoundationWeightUtility: 0.4,       // MINE_LAYER, EXCAVATOR, CAMOUFLAGE
  stabFoundationWeightRepair: 0.5,        // REPAIR_MODULE

  // ── Easter Eggs (toggle individual easter eggs on/off) ──
  eeRageQuit: true,           // #4 — Rage Quit Detector (loss streak sarcasm)
  eeConsoleNarrator: true,    // #5 — Dev Console funny responses
  eeSpeedMeltdown: true,      // #6 — Speed x999 thermal throttle
  eePacifist: true,           // #7 — Pacifist Run detection & messages
  eeStackOverflow: true,      // #10 — Tall robot "structural integrity" warning
  eeNameGame: true,           // #12 — Special robot name reactions
};

const STORAGE_KEY = 'robogame_godmode';

// ── Per-SFX volume multipliers (stored alongside GOD params) ──
export const GOD_SFX: Record<string, number> = {};

/** Default values — used for reset and as reference */
const DEFAULTS: Readonly<typeof GOD> = {
  robotHpMult: 1.0,
  robotEnergyMult: 1.0,
  robotEnergyDrainMult: 1.0,
  robotSpeedMult: 1.0,
  robotDamageMult: 1.0,
  robotPassiveRechargeRate: 0.01,
  kamikazeDetonationFactor: 0.35,
  kamikazeAoeRadiusMult: 1.0,
  kamikazeFalloffPower: 1.0,
  enemyHpMult: 1.0,
  enemySpeedMult: 1.0,
  enemyDamageMult: 1.0,
  enemyWeaponRangeMult: 1.0,
  closeInDelay: 1.5,
  closeInMaxShrink: 0.4,
  closeInRampTime: 2.0,
  enemySteerSmoothing: 0.15,
  wallDisengageTime: 3.0,
  baseTurretDamageMult: 1.0,
  baseTurretRangeMult: 1.0,
  baseUpgradeCostMult: 1.0,
  armorShieldMult: 1.0,
  armorRicochetMult: 1.0,
  tierMassMult: 0.3,
  tierEnergyDrainMult: 0.2,
  startingMoney: 70,
  challengeStartingMoney: 70,
  challengeRewardBase: 30,
  challengeRewardPerWave: 8,
  challengeResourceMult: 0.5,
  resourcesMult: 1.0,
  excavatorDpsMult: 1.0,
  resourceWeaponResist: 0.95,
  componentCostMult: 1.0,
  modificationFeePercent: 0.20,
  modificationFeeMin: 5,
  tileRepairCostMult: 1.0,
  workshopPriceMult: 1.0,
  spawnDelayMult: 1.0,
  waveCountMult: 1.0,
  tileSaturation: 1.0,
  tileBrightness: 0.0,
  tileContrast: 1.0,
  tileHue: 0.0,
  tileDarken: 0.3,
  decorSaturation: 1.0,
  decorBrightness: 0.0,
  decorContrast: 1.0,
  decorHue: 0.0,
  decorDarken: 0.0,
  resSaturation: 1.0,
  resBrightness: 0.0,
  resContrast: 1.0,
  resHue: 0.0,
  resDarken: 0.0,
  enemyAnimations: true,
  enemyScaleMult: 1.0,
  robotScaleMult: 1.0,
  componentSpacingMult: 1.0,
  robotBarOffsetMult: 1.0,
  robotBarSizeMult: 1.0,
  enemyBarOffsetMult: 1.0,
  enemyBarSizeMult: 1.0,
  flipCooldownMs: 150,
  flipDeadzone: 6,
  popupStartOffsetMult: 1.0,
  popupSizeMult: 1.0,
  popupDistMult: 1.0,
  popupDurationMult: 1.0,
  coinFlyPopHeight: 40,
  coinFlyBounceCount: 3,
  coinFlyBounceDuration: 180,
  coinFlyBounceDamping: 0.55,
  coinFlySpinSpeed: 3,
  coinFlyDuration: 500,
  coinFlyArcHeight: 80,
  coinFlyIconSize: 40,
  coinFlyHudPulseScale: 1.3,
  coinFlyStaggerDelay: 50,
  repairPanTime: 300,
  repairShowTime: 500,
  repairZoom: 2.5,
  waveEnergyRecharge: 0.50,
  waveHpRecharge: 0.15,
  cameraZoomMult: 1.0,
  cameraContestedRatio: 0.6,
  cameraFollowZoom: 2.0,
  cameraFollowLerp: 3.0,
  impactSlowmo: true,
  useSpriteUI: false,
  hudEnemiesX: 120,
  hudBaseX: 250,
  hudBarX: 310,
  hudBarW: 90,
  hudBarH: 14,
  baseHpSegmentPct: 10,
  hpBarFrameW: 240,
  hpBarFrameH: 44,
  hpBarFrameOffX: 0,
  hpBarFrameOffY: 0,
  projectileScaleMult: 1.0,
  trailLengthMult: 1.0,
  showHeatmap: false,
  showEdgeZones: false,
  showMissileArc: false,
  baseDeployRange: 3,
  eyesightRange: 57,
  lowEnergyThreshold: 0.20,
  lowEnergySpeedMult: 0.10,
  flyerAudacity: 2.0,
  flyerEdgeRepulsion: 200,
  flyerEdgeMargin: 3,
  flyAltitudeSpeed: 1.2,
  flyAltitudeScaleAmp: 0.06,
  flyAltitudeShadowAmp: 6,
  infiniteEnergy: false,
  infiniteHp: false,
  oneHitKill: false,
  unlockAll: false,
  freeBuilding: false,
  cpuBot: false,
  forceTutorial: false,
  cinematicPanTime: 800,
  cinematicBubbleDuration: 3000,
  cinematicCursorSpeed: 400,
  cinematicSpeedMult: 1.0,
  forceCinematic: false,
  billiardEnabled: true,
  billiardFriction: 3.0,
  billiardWallDamping: 0.7,
  billiardEnemyDamping: 0.85,
  billiardCollisionDamage: 0.1,
  billiardMaxBounces: 5,
  billiardMaxChainDepth: 3,
  billiardMaxSimTime: 2.0,
  billiardHeavyResistMult: 0.3,
  billiardMinSpeed: 5,
  billiardSpeedMult: 2.5,
  billiardSpreadMinDeg: 5,
  billiardSpreadMaxDeg: 20,
  mudSplashRadius: 30,
  mudTileDuration: 0,
  mudDamageMult: 1.0,
  hiveWaspMinCount: 2,
  hiveWaspMaxCount: 4,
  hivePoisonDuration: 5,
  hivePoisonDps: 4,
  hivePoisonMaxStacks: 4,
  hiveAcidSplashRadius: 45,
  healerRange: 120,
  healerCooldown: 1.5,
  healerAmount: 12,
  healerMaxTargets: 4,
  healerSelfHeal: false,
  jumperDetectRange: 1.0,
  jumperPushTilesBase: 2,
  jumperPushTilesPerLevel: 1,
  jumperMaxKnockbackTiles: 5,
  comboWindow: 2.0,
  comboMult2: 1.4,
  comboMult3: 1.8,
  comboMult4: 2.5,
  pathRecalcInterval: 0.5,
  terrainDeathWater: 2.5,
  terrainDeathMagma: 3.0,
  terrainDeathChasm: 1.0,
  meleeRangeMult: 1.5,
  enemyRobotGap: 8,
  rangedKeepDistPx: 40,
  weaponCooldownStagger: 0.5,
  evoFireRateScale: 0.5,
  evoSupportRangeBase: 1.5,
  evoSupportDrainBase: 0.85,
  sensorRangeMult0: 1.5,
  sensorRangeMult1: 1.6,
  sensorRangeMult2: 1.8,
  sensorAccBonus0: 0.2,
  sensorAccBonus1: 0.3,
  sensorAccBonus2: 0.4,
  detectionRangeMult: 1.5,
  stabFoundationEnabled: true,
  stabFoundationMin: 0.2,
  stabFoundationMax: 1.15,
  stabFoundationMobileInfluence: 0.3,
  stabFoundationWeightStructure: 1.2,
  stabFoundationWeightBracket: 1.1,
  stabFoundationWeightBattery: 1.1,
  stabFoundationWeightNeutral: 1.0,
  stabFoundationWeightSensor: 0.4,
  stabFoundationWeightWeapon: 0.3,
  stabFoundationWeightUtility: 0.4,
  stabFoundationWeightRepair: 0.5,
  eeRageQuit: true,
  eeConsoleNarrator: true,
  eeSpeedMeltdown: true,
  eePacifist: true,
  eeStackOverflow: true,
  eeNameGame: true,
};

// ── Per-enemy-type scale overrides ──
// Keyed by EnemyType string value (e.g. 'grunt', 'tank').
// Value = multiplier on that enemy type's display size (default 1.0).
// Stored separately so it auto-adapts when new enemy types are added.
const ENEMY_SCALE_KEY = 'robogame_enemy_scales';

export const ENEMY_TYPE_SCALES: Record<string, number> = {};

export function getEnemyTypeScale(type: string): number {
  return ENEMY_TYPE_SCALES[type] ?? 1.0;
}

export function setEnemyTypeScale(type: string, scale: number): void {
  ENEMY_TYPE_SCALES[type] = scale;
  saveEnemyTypeScales();
}

export function resetEnemyTypeScales(): void {
  for (const key of Object.keys(ENEMY_TYPE_SCALES)) {
    delete ENEMY_TYPE_SCALES[key];
  }
  saveEnemyTypeScales();
}

export function saveEnemyTypeScales(): void {
  try {
    localStorage.setItem(ENEMY_SCALE_KEY, JSON.stringify(ENEMY_TYPE_SCALES));
  } catch (e) { console.warn('[GodMode] saveEnemyTypeScales failed:', e); }
  scheduleSyncDefaultProfile();
}

export function loadEnemyTypeScales(): void {
  try {
    const raw = localStorage.getItem(ENEMY_SCALE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    for (const [k, v] of Object.entries(data)) {
      if (typeof v === 'number') ENEMY_TYPE_SCALES[k] = v;
    }
  } catch (e) { console.warn('[GodMode] loadEnemyTypeScales failed:', e); }
}

// ── Per-enemy-type projectile anchor offsets ──
// Offset in local sprite space (pixels relative to sprite center, before scaling).
// Used to position the projectile spawn point (muzzle) for each enemy type.
const ENEMY_ANCHOR_KEY = 'robogame_enemy_anchors';

export const ENEMY_TYPE_ANCHORS: Record<string, { ox: number; oy: number }> = {};

export function getEnemyAnchor(type: string): { ox: number; oy: number } {
  return ENEMY_TYPE_ANCHORS[type] ?? { ox: 0, oy: 0 };
}

export function setEnemyAnchor(type: string, ox: number, oy: number): void {
  ENEMY_TYPE_ANCHORS[type] = { ox, oy };
  saveEnemyAnchors();
}

export function resetEnemyAnchors(): void {
  for (const key of Object.keys(ENEMY_TYPE_ANCHORS)) {
    delete ENEMY_TYPE_ANCHORS[key];
  }
  saveEnemyAnchors();
}

export function saveEnemyAnchors(): void {
  try {
    localStorage.setItem(ENEMY_ANCHOR_KEY, JSON.stringify(ENEMY_TYPE_ANCHORS));
  } catch (e) { console.warn('[GodMode] saveEnemyAnchors failed:', e); }
  scheduleSyncDefaultProfile();
}

export function loadEnemyAnchors(): void {
  try {
    const raw = localStorage.getItem(ENEMY_ANCHOR_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    for (const [k, v] of Object.entries(data)) {
      if (v && typeof v === 'object' && 'ox' in (v as any) && 'oy' in (v as any)) {
        ENEMY_TYPE_ANCHORS[k] = v as { ox: number; oy: number };
      }
    }
  } catch (e) { console.warn('[GodMode] loadEnemyAnchors failed:', e); }
}

// ── Per-enemy-type stat overrides (balance tuning) ──
// Per-type multipliers for HP, speed, damage, range.
// Applied ON TOP of global GOD multipliers: final = base × GOD.mult × typeMult
const ENEMY_STAT_OVERRIDE_KEY = 'robogame_enemy_stat_overrides';

export interface EnemyStatOverride {
  hpMult: number;
  speedMult: number;
  damageMult: number;
  meleeDamageMult: number;
  weaponDamageMult: number;
  rangeMult: number;
  healMult: number;
}

const ENEMY_STAT_OVERRIDE_DEFAULTS: EnemyStatOverride = {
  hpMult: 1, speedMult: 1, damageMult: 1, meleeDamageMult: 1, weaponDamageMult: 1, rangeMult: 1, healMult: 1,
};

export const ENEMY_TYPE_STAT_OVERRIDES: Record<string, Partial<EnemyStatOverride>> = {};

export function getEnemyTypeStatOverride(type: string): EnemyStatOverride {
  const ov = ENEMY_TYPE_STAT_OVERRIDES[type];
  return ov ? { ...ENEMY_STAT_OVERRIDE_DEFAULTS, ...ov } : { ...ENEMY_STAT_OVERRIDE_DEFAULTS };
}

export function setEnemyTypeStatOverride(type: string, overrides: Partial<EnemyStatOverride>): void {
  // Only store non-default values
  const clean: Partial<EnemyStatOverride> = {};
  for (const k of Object.keys(overrides) as (keyof EnemyStatOverride)[]) {
    if (overrides[k] !== undefined && overrides[k] !== ENEMY_STAT_OVERRIDE_DEFAULTS[k]) {
      clean[k] = overrides[k];
    }
  }
  if (Object.keys(clean).length > 0) {
    ENEMY_TYPE_STAT_OVERRIDES[type] = clean;
  } else {
    delete ENEMY_TYPE_STAT_OVERRIDES[type];
  }
  saveEnemyTypeStatOverrides();
}

export function resetEnemyTypeStatOverrides(): void {
  for (const key of Object.keys(ENEMY_TYPE_STAT_OVERRIDES)) {
    delete ENEMY_TYPE_STAT_OVERRIDES[key];
  }
  saveEnemyTypeStatOverrides();
}

export function saveEnemyTypeStatOverrides(): void {
  try {
    localStorage.setItem(ENEMY_STAT_OVERRIDE_KEY, JSON.stringify(ENEMY_TYPE_STAT_OVERRIDES));
  } catch (e) { console.warn('[GodMode] saveEnemyTypeStatOverrides failed:', e); }
  scheduleSyncDefaultProfile();
}

export function loadEnemyTypeStatOverrides(): void {
  try {
    const raw = localStorage.getItem(ENEMY_STAT_OVERRIDE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    for (const [k, v] of Object.entries(data)) {
      if (v && typeof v === 'object') {
        ENEMY_TYPE_STAT_OVERRIDES[k] = v as Partial<EnemyStatOverride>;
      }
    }
  } catch (e) { console.warn('[GodMode] loadEnemyTypeStatOverrides failed:', e); }
}

/** Reset all GOD values to defaults and save */
export function resetGodMode(): void {
  Object.assign(GOD, DEFAULTS);
  saveGodMode();
}

/** Save current GOD values + SFX volumes to localStorage and log to console */
export function saveGodMode(): void {
  try {
    const bundle = { ...GOD, _sfxVolumes: { ...GOD_SFX } };
    const json = JSON.stringify(bundle, null, 2);
    localStorage.setItem(STORAGE_KEY, json);
  } catch (e) { console.warn('[GodMode] save failed:', e); }
  scheduleSyncDefaultProfile();
}

/** Load GOD values from localStorage (called once at boot) */
export function loadGodMode(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    for (const key of Object.keys(DEFAULTS) as (keyof typeof GOD)[]) {
      if (key in data && typeof data[key] === typeof DEFAULTS[key]) {
        (GOD as any)[key] = data[key];
      }
    }
    // Restore SFX volumes
    if (data._sfxVolumes && typeof data._sfxVolumes === 'object') {
      Object.keys(GOD_SFX).forEach(k => delete GOD_SFX[k]);
      Object.assign(GOD_SFX, data._sfxVolumes);
    }
  } catch (e) { console.warn('[GodMode] loadGodMode failed:', e); }
}

/**
 * Export GOD values as a clean JSON string (only non-default values included).
 * Use this to extract your fine-tuned calibration for production config.
 */
export function exportGodModeJSON(): string {
  const out: Record<string, number | boolean> = {};
  for (const key of Object.keys(GOD) as (keyof typeof GOD)[]) {
    if (GOD[key] !== DEFAULTS[key]) {
      out[key] = GOD[key];
    }
  }
  return JSON.stringify(out, null, 2);
}

/**
 * Export ALL GOD values as JSON (including defaults).
 * Full snapshot for archiving a calibration profile.
 */
export function exportGodModeFullJSON(): string {
  return JSON.stringify(GOD, null, 2);
}

/**
 * Import GOD values from a JSON string.
 * Only applies known keys with matching types.
 */
export function importGodModeJSON(json: string): boolean {
  try {
    const data = JSON.parse(json);
    for (const key of Object.keys(DEFAULTS) as (keyof typeof GOD)[]) {
      if (key in data && typeof data[key] === typeof DEFAULTS[key]) {
        (GOD as any)[key] = data[key];
      }
    }
    saveGodMode();
    return true;
  } catch (e) {
    console.warn('[GodMode] importGodModeJSON failed:', e);
    return false;
  }
}

// ══════════════════════════════════════════════════════════════
//  Full Profile Export/Import
//  Bundles: GOD params + Power Curve + SFX volumes + Enemy scales
//  + Player metadata (name, age, experience) for A/B testing analysis
// ══════════════════════════════════════════════════════════════

export interface PlayerProfile {
  _version: number;
  _exportedAt: string;         // ISO timestamp
  player: {
    name: string;
    age: number | null;
    experience: 'beginner' | 'casual' | 'regular' | 'experienced' | 'hardcore';
  };
  godParams: typeof GOD;
  enemyScales: Record<string, number>;
  curveParams: { basePower: number; growthRate: number; waveMin: number; waveMax: number };
  sfxVolumes: Record<string, number>;
  /** Component property overrides from WorkshopGOD (fine-tuning) */
  componentOverrides?: Record<string, unknown>;
  /** Per-enemy-type projectile anchor offsets */
  enemyAnchors?: Record<string, { ox: number; oy: number }>;
  /** Per-enemy-type stat overrides (balance tuning) */
  enemyStatOverrides?: Record<string, Partial<EnemyStatOverride>>;
  /** Game settings: CRT, volumes, difficulty (from SaveManager) */
  gameSettings?: {
    crtEnabled: boolean;
    crtScanlines: number;
    crtWarp: number;
    crtVignette: number;
    musicVolume: number;
    sfxVolume: number;
    difficultyMult: number;
  };
}

const PROFILE_VERSION = 2;
const PROFILE_VERSION_KEY = 'robogame_profile_version';
const PROFILE_PLAYER_KEY = 'robogame_player_meta';

/** Load saved player meta from localStorage */
export function loadPlayerMeta(): PlayerProfile['player'] {
  try {
    const raw = localStorage.getItem(PROFILE_PLAYER_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { console.warn('[GodMode] loadPlayerMeta failed:', e); }
  return { name: '', age: null, experience: 'casual' };
}

/** Save player meta to localStorage */
export function savePlayerMeta(meta: PlayerProfile['player']): void {
  try { localStorage.setItem(PROFILE_PLAYER_KEY, JSON.stringify(meta)); } catch (e) { console.warn('[GodMode] savePlayerMeta failed:', e); }
}

/**
 * Build a full profile JSON combining all tunable parameters.
 * Requires external data passed in (SFX volumes, curve params) to avoid circular imports.
 */
export function buildFullProfile(
  sfxVolumes: Record<string, number>,
  curveParams: { basePower: number; growthRate: number; waveMin: number; waveMax: number },
): PlayerProfile {
  const meta = loadPlayerMeta();

  // Read game settings from localStorage (avoid circular dep with SaveManager)
  let gameSettings: PlayerProfile['gameSettings'];
  try {
    const raw = localStorage.getItem('robogame_settings');
    if (raw) {
      const parsed = JSON.parse(raw);
      const s = parsed.settings ?? {};
      gameSettings = {
        crtEnabled: s.crtEnabled ?? false,
        crtScanlines: s.crtScanlines ?? 0.7,
        crtWarp: s.crtWarp ?? 0.6,
        crtVignette: s.crtVignette ?? 0.5,
        musicVolume: s.musicVolume ?? 0.2,
        sfxVolume: s.sfxVolume ?? 0.5,
        difficultyMult: s.difficultyMult ?? 1.0,
      };
    }
  } catch (e) { console.warn('[GodMode] buildFullProfile gameSettings parse failed:', e); }

  return {
    _version: PROFILE_VERSION,
    _exportedAt: new Date().toISOString(),
    player: meta,
    godParams: { ...GOD },
    enemyScales: { ...ENEMY_TYPE_SCALES },
    curveParams: { ...curveParams },
    sfxVolumes: { ...sfxVolumes },
    componentOverrides: getOverridesSnapshot(),
    enemyAnchors: { ...ENEMY_TYPE_ANCHORS },
    enemyStatOverrides: { ...ENEMY_TYPE_STAT_OVERRIDES },
    gameSettings,
  };
}

/** Serialize a profile to a JSON string ready for download */
export function exportFullProfileJSON(
  sfxVolumes: Record<string, number>,
  curveParams: { basePower: number; growthRate: number; waveMin: number; waveMax: number },
): string {
  return JSON.stringify(buildFullProfile(sfxVolumes, curveParams), null, 2);
}

/**
 * Import a full profile from JSON string.
 * Returns the parsed profile on success (caller applies SFX + curve externally).
 */
export function importFullProfile(json: string): PlayerProfile | null {
  try {
    const data = JSON.parse(json) as PlayerProfile;
    if (!data || typeof data !== 'object') return null;

    // Apply GOD params
    if (data.godParams) {
      for (const key of Object.keys(DEFAULTS) as (keyof typeof GOD)[]) {
        if (key in data.godParams && typeof data.godParams[key] === typeof DEFAULTS[key]) {
          (GOD as any)[key] = data.godParams[key];
        }
      }
      saveGodMode();
    }

    // Apply enemy scales
    if (data.enemyScales) {
      for (const key of Object.keys(ENEMY_TYPE_SCALES)) delete ENEMY_TYPE_SCALES[key];
      for (const [k, v] of Object.entries(data.enemyScales)) {
        if (typeof v === 'number') ENEMY_TYPE_SCALES[k] = v;
      }
      saveEnemyTypeScales();
    }

    // Apply player meta
    if (data.player) {
      savePlayerMeta(data.player);
    }

    // Apply component overrides (WorkshopGOD fine-tuning)
    if (data.componentOverrides && typeof data.componentOverrides === 'object') {
      importOverridesSnapshot(data.componentOverrides as Record<string, any>);
    }

    // Apply enemy anchors
    if (data.enemyAnchors && typeof data.enemyAnchors === 'object') {
      for (const key of Object.keys(ENEMY_TYPE_ANCHORS)) delete ENEMY_TYPE_ANCHORS[key];
      for (const [k, v] of Object.entries(data.enemyAnchors)) {
        if (v && typeof v === 'object' && 'ox' in (v as any) && 'oy' in (v as any)) {
          ENEMY_TYPE_ANCHORS[k] = v as { ox: number; oy: number };
        }
      }
      saveEnemyAnchors();
    }

    // Apply enemy stat overrides (balance tuning)
    if (data.enemyStatOverrides && typeof data.enemyStatOverrides === 'object') {
      for (const key of Object.keys(ENEMY_TYPE_STAT_OVERRIDES)) delete ENEMY_TYPE_STAT_OVERRIDES[key];
      for (const [k, v] of Object.entries(data.enemyStatOverrides)) {
        if (v && typeof v === 'object') {
          ENEMY_TYPE_STAT_OVERRIDES[k] = v as Partial<EnemyStatOverride>;
        }
      }
      saveEnemyTypeStatOverrides();
    }

    // Return profile so caller can apply sfxVolumes + curveParams externally
    return data;
  } catch (e) {
    console.warn('[GodMode] importFullProfile failed:', e);
    return null;
  }
}

/** Save ALL tuning systems at once (GOD, enemy scales/anchors/stats, component overrides, curve, SFX).
 *  Use this for the master SAVE button. Triggers profile sync automatically. */
export function saveAllTuning(): void {
  saveGodMode();                   // GOD params + SFX volumes
  saveEnemyTypeScales();           // per-enemy display scales
  saveEnemyAnchors();              // per-enemy projectile anchors
  saveEnemyTypeStatOverrides();    // per-enemy stat multipliers (balance)
  saveComponentOverrides();        // component property overrides (COMP GOD)
  saveCurve();                     // power curve (wave scaling)
}

// ── Auto-sync default profile to dev server ──
// Debounced: collects all save*() calls within 500ms into one POST.
// Only works in dev (Vite dev server exposes /api/save-profile).
let _syncTimer: ReturnType<typeof setTimeout> | null = null;

export function scheduleSyncDefaultProfile(): void {
  if (_syncTimer) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(() => {
    _syncTimer = null;
    _syncDefaultProfile();
  }, 500);
}

// Register so modules that can't import GodMode (circular dep) can trigger sync
registerProfileSyncCallback(scheduleSyncDefaultProfile);

function _syncDefaultProfile(): void {
  // Build profile from in-memory state (avoid circular imports for curve)
  let curveParams = { basePower: 30, growthRate: 1.06, waveMin: 5, waveMax: 22 };
  try {
    const raw = localStorage.getItem('robogame_power_curve');
    if (raw) curveParams = { ...curveParams, ...JSON.parse(raw) };
  } catch (e) { console.warn('[GodMode] _syncDefaultProfile curve parse failed:', e); }

  const profile = buildFullProfile({ ...GOD_SFX }, curveParams);
  const json = JSON.stringify(profile, null, 2);

  fetch('/api/save-profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: json,
  }).catch(() => {
    // Not in dev mode or server unavailable — silent
  });
}

/**
 * Load default-profile.json from public/ and apply it as baseline tuning
 * if no localStorage tuning exists yet (first visit on a new device / Render deploy).
 * Returns a promise that resolves when done (or immediately if localStorage already has data).
 */
export async function applyDefaultProfileIfEmpty(): Promise<void> {
  // Check stored profile version — if shipped version is newer, update tuning params
  let storedVersion = 0;
  try {
    const raw = localStorage.getItem(PROFILE_VERSION_KEY);
    if (raw) storedVersion = parseInt(raw, 10) || 0;
  } catch (e) { /* first visit */ }

  const isFirstVisit = storedVersion === 0 && ![STORAGE_KEY, ENEMY_SCALE_KEY, ENEMY_ANCHOR_KEY, ENEMY_STAT_OVERRIDE_KEY, 'robogame_comp_overrides', 'robogame_power_curve'].some(k => {
    try { return localStorage.getItem(k) !== null; } catch { return false; }
  });

  try {
    const resp = await fetch('/default-profile.json');
    if (!resp.ok) return;
    const json = await resp.text();
    const parsed = JSON.parse(json);
    const shippedVersion = parsed._version || 0;

    // Skip if already on latest version (but not first visit — those always apply)
    if (!isFirstVisit && storedVersion >= shippedVersion) return;

    const profile = importFullProfile(json);
    if (!profile) return;

    // Force-disable cheat params — dev profile may have them ON but players should not
    GOD.unlockAll = false;
    GOD.freeBuilding = false;
    GOD.infiniteEnergy = false;
    GOD.infiniteHp = false;
    GOD.oneHitKill = false;
    GOD.cpuBot = false;
    saveGodMode();

    // importFullProfile already applied: godParams, enemyScales, enemyAnchors,
    // enemyStatOverrides, componentOverrides — and saved each to localStorage.
    // We still need to apply sfxVolumes and curveParams (not handled by importFullProfile).
    if (profile.sfxVolumes) {
      Object.keys(GOD_SFX).forEach(k => delete GOD_SFX[k]);
      Object.assign(GOD_SFX, profile.sfxVolumes);
      saveGodMode();
    }
    if (profile.curveParams) {
      try {
        localStorage.setItem('robogame_power_curve', JSON.stringify(profile.curveParams));
      } catch (e) { console.warn('[GodMode] applyDefaultProfile curveParams save failed:', e); }
    }

    // Apply game settings only for first-time visitors (don't overwrite player's volume/CRT prefs)
    if (isFirstVisit && profile.gameSettings) {
      try {
        const raw = localStorage.getItem('robogame_settings');
        const data = raw ? JSON.parse(raw) : { settings: {}, scoreHistory: [] };
        data.settings = { ...data.settings, ...profile.gameSettings };
        localStorage.setItem('robogame_settings', JSON.stringify(data));
      } catch (e) { console.warn('[GodMode] applyDefaultProfile gameSettings save failed:', e); }
    }

    // Store version so we don't re-apply until next bump
    try { localStorage.setItem(PROFILE_VERSION_KEY, String(shippedVersion)); } catch { /* */ }
    console.warn(`[GodMode] Applied default-profile.json (v${shippedVersion}, ${isFirstVisit ? 'first visit' : 'version upgrade'})`);
  } catch (e) {
    console.warn('[GodMode] applyDefaultProfileIfEmpty failed:', e);
  }
}

/**
 * Force-apply the shipped default-profile.json, overwriting all current tuning.
 * Used by the "Reset to Shipped Defaults" button.
 * Returns true if successful, false otherwise.
 */
export async function forceApplyDefaultProfile(): Promise<boolean> {
  try {
    const resp = await fetch('/default-profile.json');
    if (!resp.ok) return false;
    const json = await resp.text();
    const profile = importFullProfile(json);
    if (!profile) return false;

    // Force-disable cheat params
    GOD.unlockAll = false;
    GOD.freeBuilding = false;
    GOD.infiniteEnergy = false;
    GOD.infiniteHp = false;
    GOD.oneHitKill = false;
    GOD.cpuBot = false;
    saveGodMode();

    if (profile.sfxVolumes) {
      Object.keys(GOD_SFX).forEach(k => delete GOD_SFX[k]);
      Object.assign(GOD_SFX, profile.sfxVolumes);
      saveGodMode();
    }
    if (profile.curveParams) {
      try { localStorage.setItem('robogame_power_curve', JSON.stringify(profile.curveParams)); } catch (e) { console.warn('[GodMode] forceApply curveParams save failed:', e); }
    }
    if (profile.gameSettings) {
      try {
        const raw = localStorage.getItem('robogame_settings');
        const data = raw ? JSON.parse(raw) : { settings: {}, scoreHistory: [] };
        data.settings = { ...data.settings, ...profile.gameSettings };
        localStorage.setItem('robogame_settings', JSON.stringify(data));
      } catch (e) { console.warn('[GodMode] forceApply gameSettings save failed:', e); }
    }
    console.warn('[GodMode] Force-applied default-profile.json (reset to shipped defaults)');
    return true;
  } catch (e) {
    console.warn('[GodMode] forceApplyDefaultProfile failed:', e);
    return false;
  }
}

/** Parameter descriptor for building UI */
export interface GodParam {
  key: keyof typeof GOD;
  label: string;
  category: 'robot' | 'enemy' | 'combat' | 'economy' | 'workshop' | 'wave' | 'display' | 'scale' | 'bars' | 'popup' | 'camera' | 'cheat' | 'billiard' | 'jumper' | 'hive' | 'healer' | 'profile' | 'stability' | 'mud';
  type: 'slider' | 'toggle';
  min?: number;
  max?: number;
  step?: number;
  tip?: string;
}

/** Per-layer display preset (5 values: saturation, brightness, contrast, hue, darken) */
export interface LayerPreset {
  name: string;
  color: number;
  sat: number; bright: number; contrast: number; hue: number; darken: number;
}

// Keys used to map a layer prefix to the GOD property names
export type LayerPrefix = 'tile' | 'decor' | 'res';

const BASE_PRESETS: Omit<LayerPreset, 'color'>[] = [
  { name: 'Normal',     sat: 1.0, bright: 0.0,  contrast: 1.0, hue: 0,   darken: 0.0  },
  { name: 'Dark',       sat: 0.7, bright: -0.3, contrast: 1.3, hue: 0,   darken: 0.5  },
  { name: 'Vivid',      sat: 2.2, bright: 0.15, contrast: 1.5, hue: 0,   darken: 0.0  },
  { name: 'Night',      sat: 0.3, bright: -0.4, contrast: 0.8, hue: -30, darken: 0.6  },
  { name: 'Sepia',      sat: 0.4, bright: 0.05, contrast: 1.1, hue: 30,  darken: 0.15 },
  { name: 'Industrial', sat: 0.3, bright: -0.1, contrast: 1.8, hue: 0,   darken: 0.3  },
  { name: 'Toxic',      sat: 1.5, bright: -0.1, contrast: 1.4, hue: 60,  darken: 0.2  },
  { name: 'Infernal',   sat: 1.3, bright: -0.15,contrast: 1.6, hue: -20, darken: 0.35 },
];

const PRESET_COLORS = [0xaaaaaa, 0x4466aa, 0xff66aa, 0x2244aa, 0xcc8844, 0x888888, 0x44ff44, 0xff4422];

function buildLayerPresets(): LayerPreset[] {
  return BASE_PRESETS.map((p, i) => ({ ...p, color: PRESET_COLORS[i] }));
}

/** 8 presets shared across all layers — same set, applied per-layer */
export const LAYER_PRESETS: LayerPreset[] = buildLayerPresets();

/** Layer info for rendering preset rows per layer */
export const DISPLAY_LAYERS: { prefix: LayerPrefix; label: string; icon: string; color: number }[] = [
  { prefix: 'tile',  label: 'Tiles',       icon: '🗺', color: 0x66ddcc },
  { prefix: 'decor', label: 'Decorations', icon: '🌿', color: 0x88cc66 },
  { prefix: 'res',   label: 'Resources',   icon: '💎', color: 0xffaa44 },
];

/** Apply a preset to a specific layer */
export function applyLayerPreset(prefix: LayerPrefix, preset: LayerPreset): void {
  (GOD as any)[`${prefix}Saturation`] = preset.sat;
  (GOD as any)[`${prefix}Brightness`] = preset.bright;
  (GOD as any)[`${prefix}Contrast`] = preset.contrast;
  (GOD as any)[`${prefix}Hue`] = preset.hue;
  (GOD as any)[`${prefix}Darken`] = preset.darken;
  saveGodMode();
}

export const GOD_PARAMS: GodParam[] = [
  // Robot
  { key: 'robotHpMult',          label: 'Robot HP',        category: 'robot',   type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ robot plus résistant\n↓ robot plus fragile' },
  { key: 'robotEnergyMult',     label: 'Energy Cap',      category: 'robot',   type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ plus de réserve d\'énergie\n↓ moins d\'énergie max' },
  { key: 'robotEnergyDrainMult',label: 'Energy Drain',    category: 'robot',   type: 'slider', min: 0.1, max: 3, step: 0.1, tip: '↑ consomme plus vite l\'énergie\n↓ composants moins gourmands' },
  { key: 'robotSpeedMult',      label: 'Robot Speed',     category: 'robot',   type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ robot plus rapide\n↓ robot plus lent' },
  { key: 'robotDamageMult',     label: 'Robot Damage',    category: 'robot',   type: 'slider', min: 0.1, max: 10, step: 0.1, tip: '↑ armes du robot plus puissantes\n↓ moins de dégâts' },
  { key: 'robotPassiveRechargeRate', label: 'Passive Recharge %/s', category: 'robot', type: 'slider', min: 0, max: 0.1, step: 0.005, tip: '↑ recharge passive plus rapide\n↓ dépend plus des batteries' },
  { key: 'lowEnergyThreshold',    label: 'Low Energy Threshold',  category: 'robot', type: 'slider', min: 0.05, max: 0.5, step: 0.05, tip: '↑ ralentit plus tôt (énergie encore haute)\n↓ ralentit seulement à énergie très basse' },
  { key: 'lowEnergySpeedMult',    label: 'Low Energy Speed %',    category: 'robot', type: 'slider', min: 0.05, max: 1.0, step: 0.05, tip: '↑ garde plus de vitesse en énergie basse\n↓ quasi immobile en énergie basse' },
  { key: 'kamikazeDetonationFactor', label: 'Kamikaze Detonate Dist', category: 'robot', type: 'slider', min: 0.1, max: 1.0, step: 0.05, tip: '↑ explose de plus loin\n↓ doit être plus proche pour exploser' },
  { key: 'kamikazeAoeRadiusMult',   label: 'Kamikaze AoE Radius',    category: 'robot', type: 'slider', min: 0.5, max: 5.0, step: 0.25, tip: '↑ explosion plus large\n↓ explosion plus concentrée' },
  { key: 'kamikazeFalloffPower',    label: 'Kamikaze Falloff Power',  category: 'robot', type: 'slider', min: 0.25, max: 3.0, step: 0.25, tip: '↑ dégâts chutent vite avec la distance\n↓ dégâts plus uniformes' },
  { key: 'tierMassMult',          label: 'Tier Mass +%',    category: 'robot',   type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ évolutions ajoutent plus de poids\n↓ évolutions plus légères' },
  { key: 'tierEnergyDrainMult',   label: 'Tier Drain +%',   category: 'robot',   type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ évolutions consomment plus\n↓ évolutions moins gourmandes' },
  { key: 'baseTurretDamageMult',  label: 'Base Fire Power', category: 'robot',   type: 'slider', min: 0.1, max: 10, step: 0.1, tip: '↑ tourelle de base plus forte\n↓ tourelle de base plus faible' },
  { key: 'baseTurretRangeMult',   label: 'Base Range',      category: 'robot',   type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ tourelle tire plus loin\n↓ portée réduite' },
  { key: 'baseUpgradeCostMult',   label: 'Base Upg Cost',   category: 'robot',   type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ upgrades base plus chères\n↓ upgrades base moins chères' },
  // Enemy
  { key: 'flipCooldownMs',      label: 'Flip Cooldown (ms)', category: 'enemy', type: 'slider', min: 0, max: 500, step: 10, tip: '↑ moins de flicker direction\n↓ réactivité direction' },
  { key: 'flipDeadzone',        label: 'Flip Deadzone (px)', category: 'enemy', type: 'slider', min: 0, max: 20, step: 1, tip: '↑ ignore petits mouvements\n↓ plus sensible' },
  { key: 'enemyHpMult',         label: 'Enemy HP',        category: 'enemy',   type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ ennemis plus résistants\n↓ ennemis meurent plus vite' },
  { key: 'enemySpeedMult',      label: 'Enemy Speed',     category: 'enemy',   type: 'slider', min: 0.1, max: 3, step: 0.1, tip: '↑ ennemis plus rapides\n↓ ennemis plus lents' },
  { key: 'enemyDamageMult',     label: 'Enemy Damage',    category: 'enemy',   type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ ennemis frappent plus fort\n↓ ennemis font moins de dégâts' },
  { key: 'enemyWeaponRangeMult',label: 'Enemy Range',     category: 'enemy',   type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ ennemis tirent de plus loin\n↓ portée ennemie réduite' },
  { key: 'closeInDelay',        label: 'Close-In Delay',  category: 'enemy',   type: 'slider', min: 0.5, max: 5, step: 0.1, tip: '↑ tolère plus longtemps avant de foncer\n↓ fonce plus vite si pas de tir' },
  { key: 'closeInMaxShrink',    label: 'Close-In Shrink', category: 'enemy',   type: 'slider', min: 0.1, max: 1, step: 0.05, tip: '↑ orbite se réduit peu\n↓ ennemis foncent très près' },
  { key: 'closeInRampTime',     label: 'Close-In Ramp',   category: 'enemy',   type: 'slider', min: 0.5, max: 5, step: 0.1, tip: '↑ approche progressive lente\n↓ approche rapide' },
  { key: 'enemySteerSmoothing', label: 'Steer Smoothing', category: 'enemy',   type: 'slider', min: 0.05, max: 1, step: 0.05, tip: '↑ réaction instantanée (flicker)\n↓ mouvements lissés et fluides' },
  { key: 'wallDisengageTime',   label: 'Wall Disengage',  category: 'enemy',   type: 'slider', min: 1, max: 10, step: 0.5, tip: '↑ attend plus avant de lâcher un robot derrière un mur\n↓ lâche vite et reprend le chemin' },
  // Hive
  { key: 'hiveWaspMinCount',     label: 'Wasp Min',            category: 'hive', type: 'slider', min: 0, max: 6, step: 1, tip: 'Min Wasps spawned on Hive death' },
  { key: 'hiveWaspMaxCount',     label: 'Wasp Max',            category: 'hive', type: 'slider', min: 1, max: 8, step: 1, tip: 'Max Wasps spawned on Hive death' },
  { key: 'hivePoisonDuration',   label: 'Poison Duration (s)', category: 'hive', type: 'slider', min: 1, max: 15, step: 0.5, tip: 'Acid poison DoT duration' },
  { key: 'hivePoisonDps',        label: 'Poison DPS',          category: 'hive', type: 'slider', min: 1, max: 20, step: 1, tip: 'Acid poison damage/s per stack' },
  { key: 'hivePoisonMaxStacks',  label: 'Max Poison Stacks',   category: 'hive', type: 'slider', min: 1, max: 8, step: 1, tip: 'Max concurrent poison stacks on a robot' },
  { key: 'hiveAcidSplashRadius', label: 'Acid Splash (px)',    category: 'hive', type: 'slider', min: 20, max: 100, step: 5, tip: 'Acid catapult AoE radius' },
  // Healer enemy
  { key: 'healerRange',       label: 'Heal Range (px)',    category: 'healer', type: 'slider', min: 40, max: 300, step: 10, tip: 'Aura radius for healing nearby allies' },
  { key: 'healerCooldown',    label: 'Heal Cooldown (s)',  category: 'healer', type: 'slider', min: 0.5, max: 5, step: 0.25, tip: 'Seconds between heal pulses' },
  { key: 'healerAmount',      label: 'Heal Amount (HP)',   category: 'healer', type: 'slider', min: 1, max: 50, step: 1, tip: 'HP restored per pulse per ally' },
  { key: 'healerMaxTargets',  label: 'Max Targets',        category: 'healer', type: 'slider', min: 1, max: 10, step: 1, tip: 'Max allies healed per pulse' },
  { key: 'healerSelfHeal',    label: 'Self Heal',          category: 'healer', type: 'toggle', tip: 'Whether healer also heals itself' },
  // Billiard Knockback
  { key: 'billiardEnabled',       label: 'Billiard Mode',       category: 'billiard', type: 'toggle', tip: 'ON: physique de billard (rebonds)\nOFF: knockback instantané' },
  { key: 'billiardFriction',      label: 'Friction',            category: 'billiard', type: 'slider', min: 0.5, max: 10, step: 0.25, tip: '↑ ennemis s\'arrêtent vite\n↓ glissent plus longtemps' },
  { key: 'billiardWallDamping',   label: 'Wall Damping',        category: 'billiard', type: 'slider', min: 0.1, max: 1.0, step: 0.05, tip: '↑ rebond mur conserve la vitesse\n↓ perd beaucoup au rebond' },
  { key: 'billiardEnemyDamping',  label: 'Enemy Damping',       category: 'billiard', type: 'slider', min: 0.1, max: 1.0, step: 0.05, tip: '↑ collision ennemi conserve la vitesse\n↓ perd beaucoup à la collision' },
  { key: 'billiardCollisionDamage',label: 'Collision Damage',   category: 'billiard', type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ collisions entre ennemis font mal\n↓ collisions inoffensives' },
  { key: 'billiardMaxBounces',    label: 'Max Bounces',         category: 'billiard', type: 'slider', min: 1, max: 15, step: 1, tip: '↑ plus de rebonds avant arrêt\n↓ s\'arrête après peu de rebonds' },
  { key: 'billiardMaxChainDepth', label: 'Max Chain Depth',     category: 'billiard', type: 'slider', min: 0, max: 10, step: 1, tip: '↑ réactions en chaîne plus profondes\n↓ moins de propagation' },
  { key: 'billiardMaxSimTime',    label: 'Max Sim Time (s)',    category: 'billiard', type: 'slider', min: 0.5, max: 5, step: 0.25, tip: '↑ knockback dure plus longtemps\n↓ coupe vite la simulation' },
  { key: 'billiardHeavyResistMult',label: 'Heavy Resist Mult',  category: 'billiard', type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ ennemis lourds bougent plus\n↓ ennemis lourds quasi immobiles' },
  { key: 'billiardMinSpeed',      label: 'Min Speed (px/s)',    category: 'billiard', type: 'slider', min: 1, max: 30, step: 1, tip: '↑ s\'arrête à vitesse plus haute\n↓ glisse jusqu\'à quasi immobile' },
  { key: 'billiardSpeedMult',    label: 'Speed Mult',          category: 'billiard', type: 'slider', min: 0.5, max: 5, step: 0.25, tip: '↑ knockback plus violent\n↓ poussée plus douce' },
  { key: 'billiardSpreadMinDeg', label: 'Spread Min (deg)',    category: 'billiard', type: 'slider', min: 0, max: 45, step: 1, tip: '↑ déviation angulaire minimum\n↓ poussée plus droite' },
  { key: 'billiardSpreadMaxDeg', label: 'Spread Max (deg)',    category: 'billiard', type: 'slider', min: 0, max: 45, step: 1, tip: '↑ dispersion maximale\n↓ direction plus prévisible' },
  // Mud Launcher
  { key: 'mudSplashRadius',  label: 'Splash Radius (px)', category: 'mud', type: 'slider', min: 10, max: 80, step: 5, tip: '↑ plus de tiles converties en boue\n↓ zone d\'impact réduite' },
  { key: 'mudTileDuration',  label: 'Mud Duration (s)',   category: 'mud', type: 'slider', min: 0, max: 60, step: 5, tip: '0 = permanent\n↑ boue dure plus longtemps' },
  { key: 'mudDamageMult',    label: 'Damage Mult',        category: 'mud', type: 'slider', min: 0, max: 3, step: 0.1, tip: '↑ plus de dégâts\n↓ moins de dégâts' },
  // Stability: Foundation
  { key: 'stabFoundationEnabled',         label: 'Foundation Penalty',     category: 'stability', type: 'toggle', tip: 'ON: composants à la base affectent la stabilité\nOFF: ancien calcul (masse seule)' },
  { key: 'stabFoundationMobileInfluence', label: 'Mobile Influence',       category: 'stability', type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ robots à roues aussi pénalisés\n↓ seules les tourelles affectées' },
  { key: 'stabFoundationMin',             label: 'Factor Min',             category: 'stability', type: 'slider', min: 0.1, max: 1, step: 0.05, tip: '↑ plancher du facteur fondation\n↓ pénalité max plus sévère' },
  { key: 'stabFoundationMax',             label: 'Factor Max',             category: 'stability', type: 'slider', min: 1, max: 1.5, step: 0.05, tip: '↑ bonus max pour base structurelle\n↓ pas de bonus' },
  { key: 'stabFoundationWeightStructure', label: 'W: Structure',           category: 'stability', type: 'slider', min: 0.5, max: 1.5, step: 0.05, tip: 'Poids fondation: Structure, Armor' },
  { key: 'stabFoundationWeightBracket',   label: 'W: Bracket',             category: 'stability', type: 'slider', min: 0.5, max: 1.5, step: 0.05, tip: 'Poids fondation: Bracket (triangle)' },
  { key: 'stabFoundationWeightBattery',   label: 'W: Battery',             category: 'stability', type: 'slider', min: 0.5, max: 1.5, step: 0.05, tip: 'Poids fondation: Battery' },
  { key: 'stabFoundationWeightNeutral',   label: 'W: Neutral',             category: 'stability', type: 'slider', min: 0.5, max: 1.5, step: 0.05, tip: 'Poids fondation: Direction, Recharge' },
  { key: 'stabFoundationWeightSensor',    label: 'W: Sensor',              category: 'stability', type: 'slider', min: 0.3, max: 1.5, step: 0.05, tip: 'Poids fondation: Sensors' },
  { key: 'stabFoundationWeightWeapon',    label: 'W: Weapon',              category: 'stability', type: 'slider', min: 0.2, max: 1.0, step: 0.05, tip: 'Poids fondation: toutes les armes' },
  { key: 'stabFoundationWeightUtility',   label: 'W: Utility',             category: 'stability', type: 'slider', min: 0.3, max: 1.5, step: 0.05, tip: 'Poids fondation: Mine, Excavator, Camouflage' },
  { key: 'stabFoundationWeightRepair',    label: 'W: Repair',              category: 'stability', type: 'slider', min: 0.3, max: 1.5, step: 0.05, tip: 'Poids fondation: Repair Module' },
  // Jumper
  { key: 'jumperDetectRange',     label: 'Detect Range (×tile)', category: 'jumper', type: 'slider', min: 0.25, max: 2.0, step: 0.25, tip: '↑ détecte ennemis plus loin\n↓ doit être très proche pour pousser' },
  { key: 'jumperPushTilesBase',   label: 'Push Tiles (base)',    category: 'jumper', type: 'slider', min: 1, max: 8, step: 1, tip: '↑ pousse plus loin (niveau 0)\n↓ poussée courte' },
  { key: 'jumperPushTilesPerLevel',label: 'Push Tiles /lvl',     category: 'jumper', type: 'slider', min: 0, max: 3, step: 1, tip: '↑ chaque évolution pousse plus loin\n↓ évolution n\'affecte pas la distance' },
  { key: 'jumperMaxKnockbackTiles',label: 'Max KB Tiles',        category: 'jumper', type: 'slider', min: 1, max: 10, step: 1, tip: '↑ distance max de poussée\n↓ cap la distance de knockback' },
  // Combat
  { key: 'comboWindow',            label: 'Combo Window (s)',      category: 'combat', type: 'slider', min: 0.5, max: 5, step: 0.25, tip: '↑ plus de temps pour enchaîner les combos\n↓ fenêtre de combo plus courte' },
  { key: 'comboMult2',             label: 'Combo x2 Mult',        category: 'combat', type: 'slider', min: 1.0, max: 5.0, step: 0.1, tip: '↑ bonus combo 2 types plus fort\n↓ bonus réduit' },
  { key: 'comboMult3',             label: 'Combo x3 Mult',        category: 'combat', type: 'slider', min: 1.0, max: 5.0, step: 0.1, tip: '↑ bonus combo 3 types plus fort\n↓ bonus réduit' },
  { key: 'comboMult4',             label: 'Combo x4+ Mult',       category: 'combat', type: 'slider', min: 1.0, max: 5.0, step: 0.1, tip: '↑ bonus combo 4+ types plus fort\n↓ bonus réduit' },
  { key: 'pathRecalcInterval',     label: 'Path Recalc (s)',       category: 'combat', type: 'slider', min: 0.1, max: 2.0, step: 0.1, tip: '↑ ennemis recalculent moins souvent\n↓ recalcul plus fréquent' },
  { key: 'terrainDeathWater',      label: 'Water Death (s)',       category: 'combat', type: 'slider', min: 0.5, max: 5.0, step: 0.25, tip: '↑ noyade plus lente\n↓ mort rapide dans l\'eau' },
  { key: 'terrainDeathMagma',      label: 'Magma Death (s)',       category: 'combat', type: 'slider', min: 0.5, max: 5.0, step: 0.25, tip: '↑ mort dans le magma plus lente\n↓ mort rapide' },
  { key: 'terrainDeathChasm',      label: 'Chasm Death (s)',       category: 'combat', type: 'slider', min: 0.25, max: 3.0, step: 0.25, tip: '↑ chute plus lente\n↓ chute instantanée' },
  { key: 'meleeRangeMult',         label: 'Melee Range Mult',      category: 'combat', type: 'slider', min: 1.0, max: 3.0, step: 0.1, tip: '↑ zone de mêlée plus large\n↓ mêlée au contact strict' },
  { key: 'enemyRobotGap',          label: 'Enemy-Robot Gap (px)',   category: 'combat', type: 'slider', min: 2, max: 20, step: 1, tip: '↑ plus d\'espace entre sprites\n↓ contact plus serré' },
  { key: 'rangedKeepDistPx',       label: 'Ranged Keep Dist (px)', category: 'combat', type: 'slider', min: 20, max: 80, step: 5, tip: '↑ ennemis à distance restent plus loin\n↓ se rapprochent davantage' },
  { key: 'weaponCooldownStagger',  label: 'Wpn Cooldown Stagger',  category: 'combat', type: 'slider', min: 0.1, max: 1.0, step: 0.05, tip: '↑ décalage initial plus grand\n↓ tirs plus synchronisés' },
  { key: 'detectionRangeMult',     label: 'Block Detect Range',    category: 'combat', type: 'slider', min: 0.5, max: 3.0, step: 0.1, tip: '↑ bloqueurs détectés plus loin\n↓ détection rapprochée' },
  // Evolution
  { key: 'evoFireRateScale',       label: 'Evo Fire Rate Scale',   category: 'combat', type: 'slider', min: 0.1, max: 1.0, step: 0.05, tip: '↑ cadence de tir évolue plus vite\n↓ cadence évolue moins' },
  { key: 'evoSupportRangeBase',    label: 'Evo Support Range',     category: 'combat', type: 'slider', min: 1.0, max: 2.5, step: 0.1, tip: '↑ modules support gagnent plus de portée/niveau\n↓ gain de portée réduit' },
  { key: 'evoSupportDrainBase',    label: 'Evo Support Drain',     category: 'combat', type: 'slider', min: 0.5, max: 1.0, step: 0.05, tip: '↑ modules support consomment plus à haut niveau\n↓ consomment moins' },
  // Sensor / Direction
  { key: 'sensorRangeMult0',       label: 'Dir Range Lv0',         category: 'combat', type: 'slider', min: 1.0, max: 3.0, step: 0.1, tip: '↑ radar lv0 voit plus loin\n↓ portée radar réduite' },
  { key: 'sensorRangeMult1',       label: 'Dir Range Lv1',         category: 'combat', type: 'slider', min: 1.0, max: 3.0, step: 0.1, tip: '↑ radar lv1 voit plus loin\n↓ portée radar réduite' },
  { key: 'sensorRangeMult2',       label: 'Dir Range Lv2',         category: 'combat', type: 'slider', min: 1.0, max: 3.0, step: 0.1, tip: '↑ radar lv2 voit plus loin\n↓ portée radar réduite' },
  { key: 'sensorAccBonus0',        label: 'Dir Acc Bonus Lv0',     category: 'combat', type: 'slider', min: 0, max: 0.8, step: 0.05, tip: '↑ précision lv0 augmentée\n↓ moins de bonus précision' },
  { key: 'sensorAccBonus1',        label: 'Dir Acc Bonus Lv1',     category: 'combat', type: 'slider', min: 0, max: 0.8, step: 0.05, tip: '↑ précision lv1 augmentée\n↓ moins de bonus précision' },
  { key: 'sensorAccBonus2',        label: 'Dir Acc Bonus Lv2',     category: 'combat', type: 'slider', min: 0, max: 0.8, step: 0.05, tip: '↑ précision lv2 augmentée\n↓ moins de bonus précision' },
  // Economy
  { key: 'startingMoney',       label: 'Campaign $',      category: 'economy', type: 'slider', min: 0, max: 500, step: 10, tip: '↑ plus d\'argent au début\n↓ début plus difficile' },
  { key: 'challengeStartingMoney', label: 'Challenge $',  category: 'economy', type: 'slider', min: 0, max: 500, step: 10, tip: '↑ plus d\'argent en challenge\n↓ challenge plus serré' },
  { key: 'challengeRewardBase',    label: 'Chal Reward $',  category: 'economy', type: 'slider', min: 0, max: 200, step: 5, tip: '↑ reward $ de base en challenge\n↓ moins de $ gagné' },
  { key: 'challengeRewardPerWave', label: 'Chal $/Wave',    category: 'economy', type: 'slider', min: 0, max: 50, step: 1, tip: '↑ plus de $ par wave complétée\n↓ moins de bonus par wave' },
  { key: 'challengeResourceMult',  label: 'Chal Res %',     category: 'economy', type: 'slider', min: 0, max: 1.0, step: 0.05, tip: '↑ plus de Co/Electro persistés\n↓ moins de ressources gardées' },
  { key: 'resourcesMult',       label: 'Rewards',         category: 'economy', type: 'slider', min: 0, max: 10, step: 0.5, tip: '↑ récompenses plus généreuses\n↓ moins de gains' },
  { key: 'excavatorDpsMult',   label: 'Excavator DPS',   category: 'economy', type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ minage plus rapide\n↓ minage plus lent' },
  { key: 'resourceWeaponResist', label: 'Res. Armor',     category: 'economy', type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ ressources résistent aux armes\n↓ armes détruisent les ressources' },
  { key: 'componentCostMult',   label: 'Build Cost',      category: 'economy', type: 'slider', min: 0, max: 3, step: 0.1, tip: '↑ composants plus chers\n↓ composants moins chers' },
  { key: 'modificationFeePercent', label: 'Modif Fee %',  category: 'economy', type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ modifier un robot posé coûte plus cher\n0 = modifications gratuites' },
  { key: 'modificationFeeMin',     label: 'Modif Fee Min', category: 'economy', type: 'slider', min: 0, max: 50, step: 1, tip: 'Coût minimum pour modifier un robot déjà posé' },
  { key: 'tileRepairCostMult', label: 'Tile Repair $',   category: 'economy', type: 'slider', min: 0, max: 5, step: 0.1, tip: '↑ réparation de tuiles plus chère\n↓ réparations moins chères' },
  // Workshop
  { key: 'workshopPriceMult',   label: 'All Prices',      category: 'workshop', type: 'slider', min: 0, max: 5, step: 0.1, tip: '↑ tout coûte plus cher au shop\n↓ prix réduits' },
  // Wave
  { key: 'spawnDelayMult',      label: 'Spawn Delay',     category: 'wave',    type: 'slider', min: 0.2, max: 5, step: 0.1, tip: '↑ ennemis arrivent plus lentement\n↓ vagues rapprochées' },
  { key: 'waveCountMult',       label: 'Enemy Count',     category: 'wave',    type: 'slider', min: 0.5, max: 3, step: 0.1, tip: '↑ plus d\'ennemis par vague\n↓ moins d\'ennemis' },
  // Display — Tiles
  { key: 'tileSaturation',       label: '🗺 Saturation',    category: 'display', type: 'slider', min: 0, max: 4, step: 0.05, tip: '↑ couleurs de tuiles plus vives\n↓ tuiles désaturées' },
  { key: 'tileBrightness',       label: '🗺 Brightness',    category: 'display', type: 'slider', min: -2, max: 2, step: 0.05, tip: '↑ tuiles plus claires\n↓ tuiles plus sombres' },
  { key: 'tileContrast',         label: '🗺 Contrast',      category: 'display', type: 'slider', min: 0, max: 4, step: 0.05, tip: '↑ contrastes plus forts\n↓ rendu plus plat' },
  { key: 'tileHue',              label: '🗺 Hue',           category: 'display', type: 'slider', min: -180, max: 180, step: 5, tip: '↑ décale les teintes vers le chaud\n↓ décale vers le froid' },
  { key: 'tileDarken',           label: '🗺 Darken',        category: 'display', type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ assombrit les tuiles\n↓ luminosité normale' },
  // Display — Decorations
  { key: 'decorSaturation',      label: '🌿 Saturation',    category: 'display', type: 'slider', min: 0, max: 4, step: 0.05, tip: '↑ décos plus colorées\n↓ décos grises' },
  { key: 'decorBrightness',      label: '🌿 Brightness',    category: 'display', type: 'slider', min: -2, max: 2, step: 0.05, tip: '↑ décos plus claires\n↓ décos plus sombres' },
  { key: 'decorContrast',        label: '🌿 Contrast',      category: 'display', type: 'slider', min: 0, max: 4, step: 0.05, tip: '↑ contrastes décos plus forts\n↓ plus plat' },
  { key: 'decorHue',             label: '🌿 Hue',           category: 'display', type: 'slider', min: -180, max: 180, step: 5, tip: '↑ teintes décos vers chaud\n↓ vers froid' },
  { key: 'decorDarken',          label: '🌿 Darken',        category: 'display', type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ assombrit les décos\n↓ luminosité normale' },
  // Display — Resources
  { key: 'resSaturation',        label: '💎 Saturation',    category: 'display', type: 'slider', min: 0, max: 4, step: 0.05, tip: '↑ ressources plus colorées\n↓ ressources grises' },
  { key: 'resBrightness',        label: '💎 Brightness',    category: 'display', type: 'slider', min: -2, max: 2, step: 0.05, tip: '↑ ressources plus claires\n↓ plus sombres' },
  { key: 'resContrast',          label: '💎 Contrast',      category: 'display', type: 'slider', min: 0, max: 4, step: 0.05, tip: '↑ contrastes ressources\n↓ plus plat' },
  { key: 'resHue',               label: '💎 Hue',           category: 'display', type: 'slider', min: -180, max: 180, step: 5, tip: '↑ teintes vers chaud\n↓ vers froid' },
  { key: 'resDarken',            label: '💎 Darken',        category: 'display', type: 'slider', min: 0, max: 1, step: 0.05, tip: '↑ assombrit ressources\n↓ luminosité normale' },
  // Scale
  { key: 'enemyAnimations',     label: 'Enemy Anims',      category: 'enemy',   type: 'toggle', tip: 'ON: spritesheet walk/attack/death\nOFF: static sprites' },
  { key: 'enemyScaleMult',      label: 'Enemy Size',       category: 'scale',   type: 'slider', min: 0.3, max: 3, step: 0.1, tip: '↑ ennemis visuellement plus gros\n↓ ennemis plus petits' },
  { key: 'robotScaleMult',      label: 'Robot Size',       category: 'scale',   type: 'slider', min: 0.3, max: 3, step: 0.1, tip: '↑ robot visuellement plus gros\n↓ robot plus petit' },
  { key: 'componentSpacingMult',label: 'Comp. Spacing',    category: 'scale',   type: 'slider', min: 0.5, max: 2, step: 0.1, tip: '↑ composants plus espacés\n↓ composants plus serrés' },
  // Bars
  { key: 'hudEnemiesX',         label: 'Enemies X',         category: 'bars',   type: 'slider', min: 0, max: 400, step: 5, tip: 'Position X du label Enemies' },
  { key: 'hudBaseX',            label: 'Base X',            category: 'bars',   type: 'slider', min: 0, max: 400, step: 5, tip: 'Position X du label Base' },
  { key: 'hudBarX',             label: 'HP Bar X',          category: 'bars',   type: 'slider', min: 0, max: 500, step: 5, tip: 'Position X de la barre HP base' },
  { key: 'hudBarW',             label: 'HP Bar Width',      category: 'bars',   type: 'slider', min: 30, max: 300, step: 2, tip: 'Largeur de la barre HP segmentée' },
  { key: 'hudBarH',             label: 'HP Bar Height',     category: 'bars',   type: 'slider', min: 6, max: 40,  step: 1, tip: 'Hauteur de la barre HP segmentée' },
  { key: 'baseHpSegmentPct',    label: 'Base HP Segment %', category: 'bars',   type: 'slider', min: 5, max: 25, step: 5, tip: '↑ segments plus larges (moins de carrés)\n↓ segments plus fins (plus de carrés)' },
  { key: 'hpBarFrameW',         label: 'HP Frame Width',    category: 'bars',   type: 'slider', min: 60, max: 400, step: 2, tip: 'Largeur du cadre HP bar' },
  { key: 'hpBarFrameH',         label: 'HP Frame Height',   category: 'bars',   type: 'slider', min: 10, max: 80,  step: 1, tip: 'Hauteur du cadre HP bar' },
  { key: 'hpBarFrameOffX',      label: 'HP Frame Off X',    category: 'bars',   type: 'slider', min: -30, max: 30, step: 1, tip: 'Décalage X du cadre' },
  { key: 'hpBarFrameOffY',      label: 'HP Frame Off Y',    category: 'bars',   type: 'slider', min: -20, max: 20, step: 1, tip: 'Décalage Y du cadre' },
  { key: 'robotBarOffsetMult',  label: 'Robot Bar Offset', category: 'bars',    type: 'slider', min: 0.3, max: 3, step: 0.1, tip: '↑ barre HP robot plus haute\n↓ barre plus proche du robot' },
  { key: 'robotBarSizeMult',    label: 'Robot Bar Size',   category: 'bars',    type: 'slider', min: 0.5, max: 3, step: 0.1, tip: '↑ barre HP robot plus grande\n↓ barre plus discrète' },
  { key: 'enemyBarOffsetMult',  label: 'Enemy Bar Offset', category: 'bars',    type: 'slider', min: 0.3, max: 3, step: 0.1, tip: '↑ barre HP ennemi plus haute\n↓ barre plus proche' },
  { key: 'enemyBarSizeMult',    label: 'Enemy Bar Size',   category: 'bars',    type: 'slider', min: 0.5, max: 3, step: 0.1, tip: '↑ barre HP ennemi plus grande\n↓ plus discrète' },
  // Popup
  { key: 'popupStartOffsetMult',label: 'Popup Origin',     category: 'popup',   type: 'slider', min: 0.3, max: 3, step: 0.1, tip: '↑ popup apparaît plus haut\n↓ apparaît plus bas' },
  { key: 'popupSizeMult',       label: 'Popup Size',       category: 'popup',   type: 'slider', min: 0.3, max: 2, step: 0.1, tip: '↑ texte popup plus gros\n↓ texte plus petit' },
  { key: 'popupDistMult',       label: 'Popup Distance',   category: 'popup',   type: 'slider', min: 0.3, max: 3, step: 0.1, tip: '↑ popup monte plus haut\n↓ monte moins' },
  { key: 'popupDurationMult',   label: 'Popup Duration',   category: 'popup',   type: 'slider', min: 0.3, max: 3, step: 0.1, tip: '↑ popup reste plus longtemps\n↓ disparaît plus vite' },
  // Coin fly-to-HUD
  { key: 'coinFlyPopHeight',     label: 'Coin Pop Height',  category: 'popup',   type: 'slider', min: 10, max: 100, step: 5, tip: '↑ pièce saute plus haut\n↓ petit saut' },
  { key: 'coinFlyBounceCount',   label: 'Coin Bounces',     category: 'popup',   type: 'slider', min: 1, max: 6, step: 1, tip: '↑ plus de rebonds\n↓ moins de rebonds' },
  { key: 'coinFlyBounceDuration',label: 'Bounce Speed',     category: 'popup',   type: 'slider', min: 80, max: 400, step: 20, tip: '↑ rebonds plus lents\n↓ rebonds rapides' },
  { key: 'coinFlyBounceDamping', label: 'Bounce Damping',   category: 'popup',   type: 'slider', min: 0.2, max: 0.9, step: 0.05, tip: '↑ rebonds plus hauts\n↓ amortissement rapide' },
  { key: 'coinFlySpinSpeed',     label: 'Coin Spin Speed',  category: 'popup',   type: 'slider', min: 0, max: 8, step: 0.5, tip: '↑ rotation 3D rapide\n↓ pas de rotation' },
  { key: 'coinFlyDuration',     label: 'Coin Fly Time',    category: 'popup',   type: 'slider', min: 200, max: 1200, step: 50, tip: '↑ vol plus long\n↓ vol plus rapide' },
  { key: 'coinFlyArcHeight',    label: 'Coin Arc Height',  category: 'popup',   type: 'slider', min: 0, max: 200, step: 10, tip: '↑ arc plus haut\n↓ vol en ligne droite' },
  { key: 'coinFlyIconSize',     label: 'Coin Fly Size',    category: 'popup',   type: 'slider', min: 10, max: 40, step: 2, tip: '↑ pièce plus grosse\n↓ plus discrète' },
  { key: 'coinFlyHudPulseScale',label: 'HUD Coin Pulse',   category: 'popup',   type: 'slider', min: 1.0, max: 2.0, step: 0.1, tip: '↑ pulse HUD plus gros\n↓ pulse subtil' },
  { key: 'coinFlyStaggerDelay', label: 'Coin Stagger',     category: 'popup',   type: 'slider', min: 0, max: 200, step: 10, tip: '↑ pièces plus espacées\n↓ pièces simultanées' },
  // Camera
  { key: 'cameraZoomMult',      label: 'Camera Zoom',      category: 'camera',  type: 'slider', min: 0.5, max: 3, step: 0.1, tip: '↑ vue plus zoomée\n↓ vue plus large' },
  { key: 'cameraContestedRatio',label: 'Contested Ratio',  category: 'camera',  type: 'slider', min: 0.1, max: 1.0, step: 0.05, tip: '↑ caméra suit plus le combat\n↓ caméra reste centrée' },
  { key: 'cameraFollowZoom',   label: 'Follow Zoom',      category: 'camera',  type: 'slider', min: 1.0, max: 4.0, step: 0.1, tip: '↑ zoom plus fort sur robot suivi\n↓ vue plus large' },
  { key: 'cameraFollowLerp',   label: 'Follow Lerp',      category: 'camera',  type: 'slider', min: 1.0, max: 8.0, step: 0.5, tip: '↑ suivi plus réactif\n↓ suivi plus doux' },
  { key: 'repairPanTime',       label: 'Repair Pan (ms)',  category: 'camera',  type: 'slider', min: 100, max: 1000, step: 50, tip: '↑ pan vers réparation plus lent\n↓ pan instantané' },
  { key: 'repairShowTime',      label: 'Repair Show (ms)', category: 'camera',  type: 'slider', min: 200, max: 2000, step: 50, tip: '↑ montre la réparation plus longtemps\n↓ affichage bref' },
  { key: 'repairZoom',          label: 'Repair Zoom',      category: 'camera',  type: 'slider', min: 1, max: 5, step: 0.1, tip: '↑ zoom réparation plus fort\n↓ moins de zoom' },
  { key: 'waveEnergyRecharge',  label: 'Wave EN Recharge', category: 'camera',  type: 'slider', min: 0, max: 1.0, step: 0.05, tip: '↑ plus d\'énergie récupérée entre waves\n↓ moins de recharge' },
  { key: 'waveHpRecharge',      label: 'Wave HP Recharge', category: 'camera',  type: 'slider', min: 0, max: 1.0, step: 0.05, tip: '↑ plus de HP récupérés entre waves\n↓ moins de soin' },
  // Projectiles
  { key: 'projectileScaleMult', label: 'Projectile Size',  category: 'scale',   type: 'slider', min: 0.1, max: 3, step: 0.1, tip: '↑ projectiles plus gros\n↓ projectiles plus petits' },
  { key: 'trailLengthMult',     label: 'Trail Length',     category: 'scale',   type: 'slider', min: 0.1, max: 5, step: 0.1, tip: '↑ traînées plus longues\n↓ traînées courtes' },
  // Debug
  { key: 'impactSlowmo',        label: 'Impact Slow-Mo',  category: 'camera',  type: 'toggle', tip: 'ON: ralenti à chaque impact\nOFF: vitesse normale' },
  { key: 'useSpriteUI',         label: 'Sprite UI',       category: 'display', type: 'toggle', tip: 'ON: pixel art UI (PixelLab)\nOFF: procedural tech UI' },
  { key: 'showHeatmap',         label: 'Action Heatmap',  category: 'camera',  type: 'toggle', tip: 'ON: affiche la heatmap des actions\nOFF: masqué' },
  { key: 'showEdgeZones',       label: 'Edge Scroll Zones', category: 'camera', type: 'toggle', tip: 'ON: montre les zones de scroll\nOFF: masqué' },
  { key: 'showMissileArc',      label: 'Missile Trajectory', category: 'camera', type: 'toggle', tip: 'ON: affiche la trajectoire missiles\nOFF: masqué' },
  // Vision
  { key: 'baseDeployRange',     label: 'Base Deploy Range', category: 'robot',  type: 'slider', min: 0, max: 10, step: 1, tip: '↑ peut poser le robot plus loin de la base\n↓ doit rester proche' },
  { key: 'eyesightRange',       label: 'Eyesight Range',  category: 'robot',   type: 'slider', min: 20, max: 200, step: 5, tip: '↑ voit plus loin sur la carte\n↓ vision réduite' },
  // Flying AI
  { key: 'flyerAudacity',       label: 'Flyer Audacity',  category: 'robot',   type: 'slider', min: 0.5, max: 5, step: 0.25, tip: '↑ volant explore loin de la base\n↓ volant reste près de la base' },
  { key: 'flyerEdgeRepulsion',  label: 'Flyer Edge Repulsion', category: 'robot', type: 'slider', min: 0, max: 500, step: 25, tip: '↑ volant évite les bords\n↓ peut aller au bord' },
  { key: 'flyerEdgeMargin',     label: 'Flyer Edge Margin',    category: 'robot', type: 'slider', min: 1, max: 8,   step: 0.5, tip: '↑ marge de bord plus grande\n↓ rase les bords' },
  { key: 'flyAltitudeSpeed',    label: 'Fly Altitude Speed',   category: 'robot', type: 'slider', min: 0.2, max: 3, step: 0.1, tip: '↑ monte/descend vite\n↓ transition lente' },
  { key: 'flyAltitudeScaleAmp', label: 'Fly Scale Amplitude',  category: 'robot', type: 'slider', min: 0, max: 0.15, step: 0.01, tip: '↑ effet visuel de hauteur amplifié\n↓ pas de changement de taille' },
  { key: 'flyAltitudeShadowAmp',label: 'Fly Shadow Amplitude', category: 'robot', type: 'slider', min: 0, max: 15, step: 1, tip: '↑ ombre bouge plus en vol\n↓ ombre fixe' },
  // Cheats
  { key: 'infiniteEnergy',      label: '∞ Energy',        category: 'cheat',   type: 'toggle', tip: 'ON: énergie infinie\nOFF: consommation normale' },
  { key: 'infiniteHp',          label: '∞ Robot HP',      category: 'cheat',   type: 'toggle', tip: 'ON: robot invincible\nOFF: HP normaux' },
  { key: 'oneHitKill',          label: 'One-Hit Kill',    category: 'cheat',   type: 'toggle', tip: 'ON: tue en un coup\nOFF: dégâts normaux' },
  { key: 'unlockAll',           label: 'Unlock All',      category: 'cheat',   type: 'toggle', tip: 'ON: tout débloqué\nOFF: progression normale' },
  { key: 'freeBuilding',        label: 'Free Build',      category: 'cheat',   type: 'toggle', tip: 'ON: construction gratuite\nOFF: coûts normaux' },
  { key: 'cpuBot',              label: 'CPU Bot',         category: 'cheat',   type: 'toggle', tip: 'ON: l\'IA joue à votre place\nOFF: contrôle manuel' },
  { key: 'forceTutorial',       label: 'Force Tutorial',  category: 'cheat',   type: 'toggle', tip: 'ON: force le tutoriel\nOFF: normal' },
  { key: 'forceCinematic',      label: 'Force Cinematic', category: 'cheat',   type: 'toggle', tip: 'ON: force la cinématique\nOFF: normal' },

  // Cinematic timing
  { key: 'cinematicPanTime',       label: 'Cine Pan Time',    category: 'camera',  type: 'slider', min: 200, max: 3000, step: 100, tip: '↑ pan cinématique plus lent\n↓ pan rapide' },
  { key: 'cinematicBubbleDuration', label: 'Cine Bubble Dur', category: 'camera',  type: 'slider', min: 500, max: 8000, step: 250, tip: '↑ bulles de dialogue plus longues\n↓ défilent plus vite' },
  { key: 'cinematicCursorSpeed',   label: 'Cine Cursor Spd',  category: 'camera',  type: 'slider', min: 100, max: 2000, step: 50, tip: '↑ curseur cinématique plus lent\n↓ curseur rapide' },
  { key: 'cinematicSpeedMult',     label: 'Cine Speed Mult',  category: 'camera',  type: 'slider', min: 0.25, max: 4, step: 0.25, tip: '↑ cinématiques accélérées\n↓ cinématiques au ralenti' },
];
